/**
 * Pure segmenter: groups classified 100 ms Int16 frames of ONE answer into upload segments
 * that never exceed 25 s (Sarvam REST rejects > 30 s per request).
 *
 * Append rule after every frame (scout-capture.md segmenting_spec):
 *  1. hard close at >= 25 s, cutting right after the quietest of the last 20 frames; the frames
 *     after the cut become the head of the next segment
 *  2. soft close at >= 20 s when the frame just appended is QUIET, or at >= 23 s when it is MID
 *  3. otherwise keep appending
 * Close on events (auto-stop, Done, cap, hidden, track ended/muted, context interrupted, dead tap)
 * via close(reason), regardless of length.
 *
 * Trimming on close: trailing QUIET frames beyond 500 ms after the last speech frame are dropped;
 * on the first UPLOADED segment of the answer, leading QUIET frames beyond 500 ms before the first
 * speech frame are dropped. Nothing is ever trimmed inside a segment.
 *
 * Drop rule: a segment with no confirmed speech is not uploaded and consumes no seq.
 */

import { FRAME_MS } from "./pcmDownsampler";
import type { FrameClass } from "./silenceDetector";

/** Hard ceiling on the leading-silence trim of the first segment, so a late
 *  speech-onset detection can never eat the candidate's opening words. */
const MAX_LEAD_TRIM_MS = 3000;

export interface SegmentInputFrame {
  pcm: Int16Array;
  rmsDb: number;
  cls: FrameClass;
  /** This frame is confirmed speech (detector result.speech). */
  speech: boolean;
  /** Earlier frames that retroactively became speech (detector result.onsetBack). */
  onsetBack?: number;
}

export type CloseReason =
  | "hard"
  | "soft-quiet"
  | "soft-mid"
  | "auto-stop"
  | "done"
  | "cap"
  | "interrupted"
  | "dead-tap";

export interface ClosedSegment {
  /** 0-based index across every closed segment of the answer, dropped ones included. */
  index: number;
  /** Upload sequence number, or null when the segment is dropped (no speech). */
  seq: number | null;
  upload: boolean;
  reason: CloseReason;
  /** Frames after trimming, in order. */
  frames: Int16Array[];
  /** Duration after trimming. */
  durationMs: number;
  /** Answer-relative offset of the first kept frame (dropped and trimmed audio included). */
  startMs: number;
  /** Confirmed speech contained in the segment. */
  speechMs: number;
  trimmedLeadingMs: number;
  trimmedTrailingMs: number;
}

export interface SegmenterOptions {
  /** Default 25000. */
  hardCloseMs?: number;
  /** Default 20000. */
  softCloseMs?: number;
  /** Default 23000. */
  midCloseMs?: number;
  /** Frames searched for the quietest cut point on a hard close. Default 20. */
  lookbackFrames?: number;
  /** Silence kept around speech when trimming. Default 500. */
  keepMs?: number;
}

interface StoredFrame {
  pcm: Int16Array;
  rmsDb: number;
  cls: FrameClass;
  speech: boolean;
}

export class Segmenter {
  readonly hardCloseFrames: number;
  readonly softCloseFrames: number;
  readonly midCloseFrames: number;
  readonly lookbackFrames: number;
  readonly keepFrames: number;

  private open: StoredFrame[] = [];
  /** Answer-relative offset of open[0]. */
  private openStartMs = 0;
  private closedCount = 0;
  private seqCounter = 0;

  constructor(opts: SegmenterOptions = {}) {
    this.hardCloseFrames = Math.round((opts.hardCloseMs ?? 25000) / FRAME_MS);
    this.softCloseFrames = Math.round((opts.softCloseMs ?? 20000) / FRAME_MS);
    this.midCloseFrames = Math.round((opts.midCloseMs ?? 23000) / FRAME_MS);
    this.lookbackFrames = opts.lookbackFrames ?? 20;
    this.keepFrames = Math.round((opts.keepMs ?? 500) / FRAME_MS);
  }

  /** Duration of the open (not yet closed) segment. */
  get openMs(): number {
    return this.open.length * FRAME_MS;
  }

  get openFrameCount(): number {
    return this.open.length;
  }

  /** Total audio consumed so far (closed + open, dropped included). */
  get consumedMs(): number {
    return this.openStartMs + this.open.length * FRAME_MS;
  }

  /** Number of segments that received a seq (uploaded or queued for upload). */
  get uploadedCount(): number {
    return this.seqCounter;
  }

  /** Reserve a seq for a segment produced outside this segmenter (MediaRecorder tier). */
  allocateSeq(): number {
    return this.seqCounter++;
  }

  /** Append a classified frame and apply the append rule. Returns the segment if one closed. */
  push(f: SegmentInputFrame): ClosedSegment | null {
    const stored: StoredFrame = { pcm: f.pcm, rmsDb: f.rmsDb, cls: f.cls, speech: f.speech };
    this.open.push(stored);
    if (f.onsetBack && f.onsetBack > 0) {
      const from = Math.max(0, this.open.length - 1 - f.onsetBack);
      for (let i = from; i < this.open.length - 1; i++) this.open[i].speech = true;
    }
    const len = this.open.length;
    if (len >= this.hardCloseFrames) {
      const start = Math.max(0, len - this.lookbackFrames);
      let minIdx = start;
      for (let i = start + 1; i < len; i++) if (this.open[i].rmsDb < this.open[minIdx].rmsDb) minIdx = i;
      const cut = minIdx + 1;
      const closed = this.open.slice(0, cut);
      const carry = this.open.slice(cut);
      const seg = this.finalize(closed, "hard");
      this.open = carry;
      return seg;
    }
    if (len >= this.softCloseFrames && f.cls === "QUIET") return this.closeAll("soft-quiet");
    if (len >= this.midCloseFrames && f.cls === "MID") return this.closeAll("soft-mid");
    return null;
  }

  /** Close the open segment on an event. Returns null when nothing is buffered. */
  close(reason: CloseReason): ClosedSegment | null {
    if (this.open.length === 0) return null;
    return this.closeAll(reason);
  }

  private closeAll(reason: CloseReason): ClosedSegment {
    const frames = this.open;
    this.open = [];
    return this.finalize(frames, reason);
  }

  private finalize(frames: StoredFrame[], reason: CloseReason): ClosedSegment {
    const index = this.closedCount++;
    const startBase = this.openStartMs;
    this.openStartMs += frames.length * FRAME_MS;

    let firstSpeech = -1;
    let lastSpeech = -1;
    let speechFrames = 0;
    for (let i = 0; i < frames.length; i++) {
      if (frames[i].speech) {
        speechFrames += 1;
        if (firstSpeech < 0) firstSpeech = i;
        lastSpeech = i;
      }
    }
    const upload = speechFrames > 0;

    let begin = 0;
    let end = frames.length;
    if (upload) {
      // Trailing trim: drop QUIET frames beyond keepFrames after the last speech frame.
      const keepEnd = lastSpeech + 1 + this.keepFrames;
      while (end > keepEnd && frames[end - 1].cls === "QUIET") end -= 1;
      // Leading trim on the first uploaded segment only, and never more than
      // MAX_LEAD_TRIM_MS: if the detector is slow to confirm onset (noisy room,
      // AGC ramp) firstSpeech can sit far into the segment, and an uncapped
      // trim would delete the candidate's opening words.
      if (this.seqCounter === 0) {
        const maxLeadFrames = Math.round(MAX_LEAD_TRIM_MS / FRAME_MS);
        const keepStart = Math.min(firstSpeech - this.keepFrames, maxLeadFrames);
        while (begin < keepStart && frames[begin].cls === "QUIET") begin += 1;
      }
    }

    const kept = frames.slice(begin, end);
    return {
      index,
      seq: upload ? this.seqCounter++ : null,
      upload,
      reason,
      frames: kept.map((s) => s.pcm),
      durationMs: kept.length * FRAME_MS,
      startMs: startBase + begin * FRAME_MS,
      speechMs: speechFrames * FRAME_MS,
      trimmedLeadingMs: begin * FRAME_MS,
      trimmedTrailingMs: (frames.length - end) * FRAME_MS,
    };
  }
}
